import { Button, Input, Form, Select } from 'antd';
import { z } from 'zod';
import { NameErrorCodes } from '@copilot/common/utils';
import { ErrorMessages } from '@copilot/common/utils/campaign';
import { useFeatureFlags } from '@copilot/common/hooks/useFeatureFlags/useFeatureFlags';
import { isNil } from 'lodash';

export type NameValidator = (nameToValidate: string) => Promise<NameErrorCodes>;

export type CampaignCreationFormProps = {
    nameValidator: NameValidator;
    onSubmit: (name: string, orgMemberId?: string) => void;
    isLoading: boolean;
    memberOptions: { label: string; value: string }[];
    defaultName?: string;
    submitButtonText?: string;
    submitTrackingID: string;
};

const campaignNameSchema = z.object({
    name: z
        .string()
        .min(1, 'Please enter a campaign name')
        .max(50, 'Must be less than 50 characters'),
});

type FormValues = {
    name: string;
    orgMemberId?: string;
};

/**
 * Form for creating a new campaign, does basic validation and passes the values to the onSubmit handler
 * @param {NameValidator} nameValidator validates the name that is entered (eg uniqueness check)
 * @param {boolean} isLoading whether we want to show loading state
 * @param {function} onSubmit called when submit is clicked
 * @param memberOptions
 * @param defaultName Default initial value for name field
 * @param submitButtonText text shown in the button for proceeding
 * @returns
 */
export function CampaignCreationForm({
    nameValidator,
    isLoading,
    onSubmit,
    memberOptions = [],
    defaultName = '',
    submitButtonText = 'Create',
    submitTrackingID,
}: CampaignCreationFormProps) {
    const [form] = Form.useForm<FormValues>();
    const { isNewCampaignCreationEndpointEnabled } = useFeatureFlags();

    function validateName(_: any, value: string) {
        if (!value) {
            return Promise.reject('Please enter a campaign name');
        }

        // First validate with Zod
        const result = campaignNameSchema.safeParse({ name: value });
        if (!result.success) {
            const error = result.error.errors[0];
            return Promise.reject(error.message);
        }

        // Then check for duplicates
        return nameValidator(value).then((code: NameErrorCodes) => {
            if (code !== NameErrorCodes.NoError) {
                return Promise.reject(ErrorMessages[code]);
            }
            return Promise.resolve();
        });
    }

    /**
     * Validates that if we have multiple members to pick from, we have selected one
     * @param _
     * @param value
     * @returns
     */
    function validateOrgMember(_: unknown, orgMemberId: string) {
        if (
            (isNewCampaignCreationEndpointEnabled &&
                isNil(orgMemberId) &&
                memberOptions.length > 1) ||
            !memberOptions.some((option) => option.value === orgMemberId)
        ) {
            return Promise.reject('Please select an org member to add to the campaign');
        }
        return Promise.resolve();
    }

    function onFinish(values: FormValues) {
        onSubmit(values.name, values.orgMemberId);
    }

    return (
        <Form
            form={form}
            onFinish={onFinish}
            layout="vertical"
            initialValues={{ name: defaultName }}
        >
            <Form.Item
                name="name"
                validateTrigger="onChange"
                rules={[{ validator: validateName }]}
                label="Campaign Name"
            >
                <Input placeholder="Enter a Campaign Name" disabled={isLoading} />
            </Form.Item>
            {isNewCampaignCreationEndpointEnabled && memberOptions.length > 1 && (
                <Form.Item
                    name="orgMemberId"
                    validateTrigger="onChange"
                    rules={[{ validator: validateOrgMember }]}
                    label="Create Campaign For..."
                >
                    <Select
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={memberOptions}
                        placeholder="Select a Team Member"
                        disabled={isLoading}
                    />
                </Form.Item>
            )}
            <Button
                type="primary"
                block
                htmlType="submit"
                loading={isLoading}
                data-tracking-id={submitTrackingID}
            >
                {submitButtonText}
            </Button>
        </Form>
    );
}
